"use client";

import { useCallback, useEffect, useRef } from "react";
import { Icon, IconName } from "../icons/Icon";
import { spacing } from "~/utils/tailwindUtils";
import Card from "../cards/Card";
import Flex from "../flex/Flex";
import { twMerge } from "tailwind-merge";

export type ModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  children: React.ReactNode;
  outsideClick?: boolean;
  size?: "sm" | "lg";
  classname?: string;
  isCloseButtonHidden?: boolean;
  modalClassname?: string;
  modalContainerClassname?: string;
  childrenDivWrapperClassName?: string;
};

/**
 * Provides a modal component that can be used to display content in a modal window with a close button.
 * The modal will close if the user clicks outside of it.
 * @param isOpen - boolean to determine if the modal is open
 * @param closeModal - function to close the modal
 * @param children - content to display in the modal
 * @param outsideClick - boolean to determine if the modal should close when clicking outside of it
 * @param size - size of the modal
 * @param modalClassname - custom class name for the backdrop (outer container)
 * @param modalContainerClassname - custom class name for the modal container (inner white box)
 * @param classname - custom class name for the Card inside modal
 * @returns Modal component
 */
const Modal = ({
  isOpen,
  closeModal,
  children,
  outsideClick,
  classname,
  isCloseButtonHidden = false,
  modalClassname,
  modalContainerClassname,
  childrenDivWrapperClassName,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size = "lg", // TODO: Fix, find a better way: https://github.com/andercore-labs/buyer-app-ui/pull/615#discussion_r1708801903
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close the modal if clicking outside of the modal
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on elements with data-product-dropdown attribute (portal-rendered dropdowns)
      if (target.closest('[data-product-dropdown]')) {
        return;
      }
      if (modalRef.current && !modalRef.current.contains(target)) {
        closeModal();
      }
    },
    [closeModal]
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Calculate scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && outsideClick) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
  }, [isOpen, outsideClick, handleOutsideClick]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return (
    <Flex
      className={twMerge(
        "fixed inset-0 items-center justify-center bg-gray-900/20 z-100 transition-all duration-300",
        isOpen ? "opacity-100 visible" : "opacity-0 invisible",
        modalClassname
      )}
    >
      <Flex
        className={twMerge(
          "flex-col relative bg-white transform transition-all duration-300 ease-out rounded-2xl",
          isOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
          modalContainerClassname
        )}
        ref={modalRef}
      >
        {!isCloseButtonHidden && (
          <Flex className="justify-end px-2 pt-2 z-90 bg-white rounded-t-2xl">
            <button
              className="text-gray-600 hover:bg-gray-50 rounded-full cursor-pointer sticky p-1.5"
              onClick={closeModal}
            >
              <Icon name={IconName.Cancel} size={spacing[5]} colorMode="fill" customStrokeWidth={2} />
            </button>
          </Flex>
        )}
        <Card
          className={twMerge(
            "w-full max-w-[90vw] md:max-w-[85vw] justify-center self-center relative p-6 pt-0 md:p-10 md:pt-0 border-0 rounded-b-2xl",
            isCloseButtonHidden ? "rounded-t-2xl" : "rounded-t-none",
            classname
          )}
        >
          <Flex className={twMerge("flex-col items-center gap-4 w-full", childrenDivWrapperClassName)}>{children}</Flex>
        </Card>
      </Flex>
    </Flex>
  );
};

export default Modal;
